import {
  ArrowDropUp as ArrowDropUpIcon,
  // ArrowDropDown as ArrowDropDownIcon,
  KeyboardTabRounded as KeyboardTabRoundedIcon,
  StartRounded as StartRoundedIcon,
  LocalShippingRounded as LocalShippingRoundedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  Add as AddIcon,
  WorkOutlineOutlined as WorkOutlineOutlinedIcon,
  Circle as CircleIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
  WarningRounded as WarningRoundedIcon,
  ArrowForwardRounded as ArrowForwardRoundedIcon,
  ArrowBackRounded as ArrowBackRoundedIcon,
  AcUnitRounded as AcUnitRoundedIcon,
  Construction as ConstructionIcon,
  ErrorRounded as ErrorRoundedIcon,
  InfoRounded as InfoRoundedIcon,
} from '@mui/icons-material';
import styled from 'styled-components';
import {
  LineIconPanel,
  DonutPanel,
  BarPanel,
  TablePanel,
  IconCircle,
  Chip,
  OperationPanel,
  TableIconCell,
  TableIconsCell,
  TableCellComment,
  MapPanel,
  MAP_PANEL_DEMO_LOCATIONS,
} from '../../design-system';
import { TableCell } from '@mui/material';
import {
  GateStatusToChipColors,
  colors,
  GateStatusToChipLabels,
  QueueStatusToChipColors,
  QueueStatusToChipLabels,
  containerTypes,
} from '../../const';
import { momentOnlyHour } from '../../utils';
import { OverviewQuery } from '../../types';
import { forecastValuesByTrend, getIconPanelGateProps } from './utils';
import moment from 'moment';
import { getGateInStatus } from '../gate-ins/utils';
import { i18n } from '../../i18n';

export const OverviewPage = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  grid-auto-flow: row;
  grid-row-gap: var(--spacing-6);

  height: max-content;
  transition: transform 0.3s;

  &::after {
    content: '';
    display: block;
    height: 0.1rem;
  }
`;

export const OverviewPanelsRow = styled.div<{ columns?: string; minRow?: string }>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: ${(props) => props.columns};
  grid-auto-columns: 1fr;
  grid-column-gap: var(--spacing-6);
  grid-template-rows: ${(props) =>
    props.minRow ? `minmax(${props.minRow}, 1fr)` : 'minmax(20rem, 1fr)'};

  /* REMOVE BOTTOM STYLES */
  & > span {
    background-color: var(--gray-80);
    border-radius: var(--border-radius-6);
  }
`;

export const OverviewPanelsRows = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: max-content;
  grid-auto-rows: 1fr;
  grid-row-gap: var(--spacing-6);
`;

interface PanelProps {
  data: OverviewQuery['data'];
}

export const GateInCompletedPanel = ({ data }: PanelProps) => {
  const gateInCompletedCountSum = data.gateInCompletedCount.reduce((acc, curr) => acc + curr, 0);
  const gateInCompletedCountSumOld = data.gateInCompletedCountOld.reduce(
    (acc, curr) => acc + curr,
    0,
  );

  const color = gateInCompletedCountSum > gateInCompletedCountSumOld ? 'lightgreen' : 'lightred';

  const { subValueLeft, subValueIcon } = getIconPanelGateProps(
    gateInCompletedCountSum,
    gateInCompletedCountSumOld,
  );

  return (
    <LineIconPanel
      title={i18n.t('pages.overview.gateInCompleted.title')}
      icon={<KeyboardTabRoundedIcon />}
      color={'lightgreen'}
      value={String(gateInCompletedCountSum)}
      subValueIcon={subValueIcon}
      subValueIconColor={color}
      subValueLeft={`${subValueLeft}%`}
      subValueRight={i18n.t('pages.overview.gateInCompleted.subValueRight')}
      chartData={data.gateInCompletedCount}
    />
  );
};

export const GateOutCompletedPanel = ({ data }: PanelProps) => {
  const gateOutCompletedCountSum = data.gateOutCompletedCount.reduce((acc, curr) => acc + curr, 0);
  const gateOutCompletedCountSumOld = data.gateOutCompletedCountOld.reduce(
    (acc, curr) => acc + curr,
    0,
  );

  const color = gateOutCompletedCountSum > gateOutCompletedCountSumOld ? 'lightgreen' : 'lightred';

  const { subValueLeft, subValueIcon } = getIconPanelGateProps(
    gateOutCompletedCountSum,
    gateOutCompletedCountSumOld,
  );

  return (
    <LineIconPanel
      title={i18n.t('pages.overview.gateOutCompleted.title')}
      icon={<StartRoundedIcon />}
      color={'lightblue'}
      value={String(gateOutCompletedCountSum)}
      subValueIcon={subValueIcon}
      subValueIconColor={color}
      subValueLeft={`${subValueLeft}%`}
      subValueRight={i18n.t('pages.overview.gateOutCompleted.subValueRight')}
      chartData={data.gateOutCompletedCount}
    />
  );
};

export const ContainersInDepotPanel = ({ data }: PanelProps) => {
  const containerCount = data.containerCount;

  const chartData = data.gateInCompletedCount.map(
    (count, i) => count - data.gateOutCompletedCount[i],
  );

  return (
    <LineIconPanel
      title={i18n.t('pages.overview.containersInDepot.title')}
      icon={<WorkOutlineOutlinedIcon />}
      color="lightpurple"
      value={containerCount}
      subValueIcon={<CircleIcon style={{ fontSize: '1.2rem' }} />}
      subValueIconColor="lightgreen"
      subValueLeft=""
      subValueRight={i18n.t('pages.overview.containersInDepot.subValueRight')}
      chartData={chartData}
    />
  );
};

export const ContainerStatusPanel = ({ data }: PanelProps) => {
  const { containerCountByStatus: categories } = data;

  return (
    <DonutPanel
      title={i18n.t('pages.overview.containerStatus.title')}
      categories={[
        { name: i18n.t('common.gatedIn'), value: categories.gateInCount },
        { name: i18n.t('common.onHold'), value: categories.onHoldCount },
        { name: i18n.t('common.forSale'), value: categories.forSaleCount },
        { name: i18n.t('common.inRepair'), value: categories.repairCount },
        { name: i18n.t('common.gateOutRequested'), value: categories.gateOutRequestedCount },
      ]}
    />
  );
};

export const ContainerTypePanel = ({ data }: PanelProps) => {
  const { containerCountByType: categories } = data;

  return (
    <DonutPanel
      title={i18n.t('pages.overview.containerType.title')}
      categories={containerTypes.map(({ label, value }) => ({
        name: label,
        value: categories[value],
      }))}
    />
  );
};

export const ContainerLocationPanel = ({ data }: PanelProps) => {
  return (
    <MapPanel
      title={i18n.t('pages.overview.containerLocation.title')}
      locations={data.containerByLocation}
      legendTitle={i18n.t('pages.overview.containerLocation.legendTitle')}
      valueMax={250}
    />
  );
};

export const GateCompletedBarPanel = ({ data }: PanelProps) => {
  const pointsCount = data.gateInCompletedCount.length;
  const now = new Date();
  const times = Array.from({ length: pointsCount }, (_, index) => {
    const d = new Date(now);
    d.setDate(now.getDate() - (pointsCount - 1 - index));
    return moment(d).format('MMM-D');
  });

  return (
    <BarPanel
      title={i18n.t('pages.overview.gateCompletedBar.title')}
      chartData={[
        {
          name: i18n.t('common.gateIn'),
          times: times,
          values: data.gateInCompletedCount,
        },
        {
          name: i18n.t('common.gateOut'),
          times: times,
          values: data.gateOutCompletedCount,
        },
      ]}
    />
  );
};

interface ExpectedGateBarPanelProps extends PanelProps {
  lastDays: number;
}

export const ExpectedGateBarPanel = ({ data, lastDays }: ExpectedGateBarPanelProps) => {
  const historyDays = Math.max(1, Math.ceil(lastDays / 2));
  const forecastDays = Math.max(1, lastDays - historyDays);
  const now = new Date();
  const historyTimes = Array.from({ length: historyDays }, (_, index) => {
    const date = new Date(now);
    date.setDate(now.getDate() - (historyDays - index - 1));
    return moment(date).format('MMM-D');
  });
  const forecastTimes = Array.from({ length: forecastDays }, (_, index) => {
    const date = new Date(now);
    date.setDate(now.getDate() + index + 1);
    return moment(date).format('MMM-D');
  });
  const gateInHistory = data.gateInCompletedCount.slice(-historyDays);
  const gateOutHistory = data.gateOutCompletedCount.slice(-historyDays);
  const gateInForecast = forecastValuesByTrend(gateInHistory, forecastDays);
  const gateOutForecast = forecastValuesByTrend(gateOutHistory, forecastDays);

  return (
    <BarPanel
      title={i18n.t('pages.overview.depotOccupancy.expectedMovesTitle', {
        lastDays: forecastDays,
      })}
      chartData={[
        {
          name: i18n.t('pages.overview.depotOccupancy.expectedIn'),
          times: [...historyTimes, ...forecastTimes],
          values: [...gateInHistory, ...gateInForecast],
        },
        {
          name: i18n.t('pages.overview.depotOccupancy.expectedOut'),
          times: [...historyTimes, ...forecastTimes],
          values: [...gateOutHistory, ...gateOutForecast],
        },
      ]}
    />
  );
};

export const LiveGateActivityTablePanel = ({ data }: PanelProps) => {
  return (
    <TablePanel
      title={i18n.t('pages.overview.liveGateActivityTable.title')}
      data={data.lastGateCompleted.map((gate) => ({
        status: gate.status === 'GATED_IN' ? 'GATED_IN' : 'GATED_OUT',
        number: gate.number,
        completedAt: gate.completedAt,
      }))}
      columns={[
        {
          body: (container) => (
            <TableCell align={'left'}>
              {container.status === 'GATED_IN' ? (
                <IconCircle $color={colors['--green-100']}>
                  <ArrowForwardRoundedIcon />
                </IconCircle>
              ) : (
                <IconCircle $color={colors['--primary-100']}>
                  <ArrowBackRoundedIcon />
                </IconCircle>
              )}
            </TableCell>
          ),
        },
        {
          body: (container) => (
            <TableCell align={'left'}>{momentOnlyHour(container.completedAt)}</TableCell>
          ),
        },
        {
          body: (container) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={
                  container.status === 'GATED_IN'
                    ? i18n.t('pages.overview.liveGateActivityTable.in')
                    : i18n.t('pages.overview.liveGateActivityTable.out')
                }
                color={container.status === 'GATED_IN' ? 'green' : 'blue'}
              />
            </TableCell>
          ),
        },
        {
          body: (container) => <TableCell align={'right'}>{container.number}</TableCell>,
        },
      ]}
    />
  );
};

export const OperationsPanel = ({ data }: PanelProps) => {
  return (
    <OperationPanel
      title={i18n.t('pages.overview.operations.title')}
      data={[
        {
          name: i18n.t('pages.overview.operations.item1.name'),
          content: i18n.t('pages.overview.operations.item1.content'),
          value: data.operations.gateOutActiveCount,
          icon: <StartRoundedIcon />,
          color: 'lightblue',
        },
        {
          name: i18n.t('pages.overview.operations.item2.name'),
          content: i18n.t('pages.overview.operations.item2.content'),
          value: data.operations.gateInExpiredCount,
          icon: <LocalShippingOutlinedIcon />,
          color: 'lightyellow',
        },
        {
          name: i18n.t('pages.overview.operations.item3.name'),
          content: i18n.t('pages.overview.operations.item3.content'),
          value: data.operations.gateOutExpiredCount,
          icon: <LocalShippingOutlinedIcon />,
          color: 'lightyellow',
        },
        {
          name: i18n.t('pages.overview.operations.item4.name'),
          content: i18n.t('pages.overview.operations.item4.content'),
          value: data.operations.repairCount,
          icon: <ConstructionIcon />,
          color: 'lightred',
        },
      ]}
    />
  );
};

export const GateTablePanel = ({ data }: PanelProps) => {
  return (
    <TablePanel
      title={i18n.t('pages.overview.gateTable.title')}
      viewAllLink="/dashboard/gateouts"
      data={data.lastGateActive.slice(0, 5).map((gate) => ({
        status: gate.status === 'IN_TRANSIT' ? 'GATED_IN' : 'GATED_OUT',
        isLoaded: gate.isLoaded,
        licensePlateNumber:
          gate.driverLicensePlateNumber || gate.authorizedLicensePlateNumbers?.join(', ') || '',
        driver: `${gate.driverFirstName || ''} ${gate.driverLastName || ''}`,
        queueStatus:
          gate.status === 'IN_TRANSIT'
            ? getGateInStatus(gate.validTo, gate.gateStatus)
            : gate.gateStatus,
      }))}
      columns={[
        {
          body: (row) => (
            <TableIconCell align="left">
              {row.isLoaded ? (
                <LocalShippingRoundedIcon
                  sx={{ color: colors['--primary-100'], fontSize: 'var(--font-size-6)' }}
                />
              ) : (
                <LocalShippingOutlinedIcon
                  sx={{ color: colors['--primary-100'], fontSize: 'var(--font-size-6)' }}
                />
              )}
            </TableIconCell>
          ),
          column: 1,
        },
        {
          headText: () => i18n.t('pages.overview.gateTable.truckNo'),
          body: (row) => <TableCellComment comment={row.licensePlateNumber} />,
          column: 9,
        },
        {
          headText: () => i18n.t('pages.overview.gateTable.operation'),
          headAlign: 'center',
          body: (row) => (
            <TableIconsCell align="left" withGap>
              {row.status === 'GATED_IN' ? (
                <KeyboardTabRoundedIcon
                  sx={{ fontSize: 'var(--font-size-6)', color: colors['--green-100'] }}
                />
              ) : (
                <StartRoundedIcon
                  sx={{ fontSize: 'var(--font-size-6)', color: colors['--primary-100'] }}
                />
              )}
              {row.status === 'GATED_IN' ? i18n.t('common.gateIn') : i18n.t('common.gateOut')}
            </TableIconsCell>
          ),
        },
        {
          headText: () => i18n.t('pages.overview.gateTable.status'),
          headAlign: 'center',
          body: (row) => (
            <TableCell align="left">
              <Chip
                size="small"
                label={
                  GateStatusToChipLabels[row.queueStatus as keyof typeof GateStatusToChipLabels]
                }
                color={
                  GateStatusToChipColors[row.queueStatus as keyof typeof GateStatusToChipColors]
                }
              />
            </TableCell>
          ),
        },
      ]}
    />
  );
};

export const AlertsPanel = ({ data }: PanelProps) => {
  return (
    <OperationPanel
      title={i18n.t('pages.overview.alerts.title')}
      data={[
        {
          name: i18n.t('pages.overview.alerts.item1.name'),
          content: i18n.t('pages.overview.alerts.item1.content', {
            count: data.alerts.containerWithoutLocationCount,
          }),
          value: data.alerts.containerWithoutLocationCount,
          icon: <WarningRoundedIcon />,
          withoutBg: true,
          color: 'lightyellow',
        },
        {
          name: i18n.t('pages.overview.alerts.item2.name'),
          content: i18n.t('pages.overview.alerts.item2.content'),
          value: data.alerts.repairClientNotAssignedCount,
          icon: <WarningRoundedIcon />,
          withoutBg: true,
          color: 'lightyellow',
        },
        {
          name: i18n.t('pages.overview.alerts.item3.name'),
          content: i18n.t('pages.overview.alerts.item3.content'),
          value: data.alerts.repairClientAssignedCount,
          icon: <InfoRoundedIcon />,
          withoutBg: true,
          color: 'lightblue',
        },
        {
          name: i18n.t('pages.overview.alerts.item4.name'),
          content: i18n.t('pages.overview.alerts.item4.content'),
          value: data.alerts.heldMoreDaysContainersCount,
          icon: <InfoRoundedIcon />,
          withoutBg: true,
          color: 'lightblue',
        },
      ]}
    />
  );
};

// EXAMPLES
export const Mock1LineIconPanel = () => {
  return (
    <LineIconPanel
      title="GATE IN TODAY"
      icon={<KeyboardTabRoundedIcon />}
      color="lightgreen"
      value="128"
      subValueIcon={<ArrowDropUpIcon />}
      subValueLeft="12%"
      subValueRight="vs yesterday"
      chartData={[5, 8, 6, 20, 30, 15, 20, 17, 13, 22, 25, 15, 30]}
    />
  );
};

export const Mock2LineIconPanel = () => {
  return (
    <LineIconPanel
      title="GATE OUT TODAY"
      icon={<StartRoundedIcon />}
      color="lightblue"
      value="96"
      subValueIcon={<ArrowDropUpIcon />}
      subValueIconColor="lightgreen"
      subValueLeft="8%"
      subValueRight="vs yesterday"
      chartData={[5, 8, 6, 20, 30, 15, 20, 17, 13, 22, 25, 15, 30]}
    />
  );
};

export const Mock3LineIconPanel = () => {
  return (
    <LineIconPanel
      title="TRUCKS WAITING"
      icon={<LocalShippingOutlinedIcon />}
      color="lightyellow"
      value="14"
      subValueIcon={<AddIcon />}
      subValueLeft="2"
      subValueRight="vs yesterday"
      chartData={[5, 8, 6, 20, 30, 15, 20, 17, 13, 22, 25, 15, 30]}
    />
  );
};

export const Mock4LineIconPanel = () => {
  return (
    <LineIconPanel
      title="CONTAINERS IN DEPOT"
      icon={<WorkOutlineOutlinedIcon />}
      color="lightpurple"
      value="5842"
      subValueIcon={<CircleIcon style={{ fontSize: '1.2rem' }} />}
      subValueIconColor="lightgreen"
      subValueLeft=""
      subValueRight="Live"
      chartData={[5, 8, 6, 20, 30, 15, 20, 17, 13, 22, 25, 15, 30]}
    />
  );
};

export const Mock5LineIconPanel = () => {
  return (
    <LineIconPanel
      title="OVERDUE CONTAINERS (DEMO)"
      icon={<WarningAmberRoundedIcon />}
      color="lightred"
      value="312"
      subValueIcon={<AddIcon />}
      subValueLeft="15%"
      subValueRight="vs yesterday"
      chartData={[5, 8, 6, 20, 30, 15, 20, 17, 13, 22, 25, 15, 30]}
    />
  );
};

export const Mock1DonutPanel = () => {
  return (
    <DonutPanel
      title="CONTAINER STATUS"
      categories={[
        { name: 'Gated In', value: 3152 },
        { name: 'On Hold', value: 1120 },
        { name: 'For Sale', value: 842 },
        { name: 'In Repair', value: 428 },
        { name: 'Other', value: 300 },
      ]}
    />
  );
};

export const Mock2DonutPanel = () => {
  return (
    <DonutPanel
      title="CONTAINER TYPE"
      categories={[
        { name: "20'DC", value: 3421 },
        { name: "20'HC", value: 1842 },
        { name: "40'DC", value: 321 },
        { name: "40'HC", value: 158 },
        { name: "45'HC", value: 100 },
      ]}
    />
  );
};

export const Mock1MapPanel = () => {
  return (
    <MapPanel
      title="CONTAINERS BY LOCATION"
      locations={MAP_PANEL_DEMO_LOCATIONS}
      legendTitle="Number of Containers"
      valueMax={250}
    />
  );
};

export const Mock1BarPanel = () => {
  return (
    <BarPanel
      title="GATE ACTIVITY (TODAY)"
      chartData={[
        {
          name: 'Gate In',
          times: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'],
          values: [5, 42, 55, 74, 52, 22, 9],
        },
        {
          name: 'Gate Out',
          times: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'],
          values: [7, 12, 26, 62, 74, 44, 7],
        },
      ]}
    />
  );
};

export const Mock1TablePanel = () => {
  return (
    <TablePanel
      title="LIVE GATE ACTIVITY"
      viewAllLink="/dashboard/containers"
      data={[
        { status: 'GATED_IN', number: 'WSCU1234567', completedAt: '2026-04-30T10:00:00.000Z' },
        { status: 'GATED_OUT', number: 'NYKU1234567', completedAt: '2026-04-30T11:00:00.000Z' },
        { status: 'GATED_IN', number: 'CMAU1234567', completedAt: '2026-04-30T12:00:00.000Z' },
        { status: 'GATED_OUT', number: 'EMCU1234567', completedAt: '2026-04-30T13:00:00.000Z' },
        { status: 'GATED_IN', number: 'TCLU1234567', completedAt: '2026-04-30T14:00:00.000Z' },
      ]}
      columns={[
        {
          body: (container) => (
            <TableCell align={'left'}>
              {container.status === 'GATED_IN' ? (
                <IconCircle $color={colors['--green-100']}>
                  <ArrowForwardRoundedIcon />
                </IconCircle>
              ) : (
                <IconCircle $color={colors['--primary-100']}>
                  <ArrowBackRoundedIcon />
                </IconCircle>
              )}
            </TableCell>
          ),
          column: 6,
        },
        {
          body: (container) => (
            <TableCell align={'left'}>{momentOnlyHour(container.completedAt)}</TableCell>
          ),
        },
        {
          body: (container) => (
            <TableCell align={'left'}>
              <Chip
                size={'small'}
                label={container.status === 'GATED_IN' ? 'in' : 'out'}
                color={container.status === 'GATED_IN' ? 'green' : 'blue'}
              />
            </TableCell>
          ),
        },
        {
          body: (container) => <TableCell align={'right'}>{container.number}</TableCell>,
        },
      ]}
    />
  );
};

export const Mock1OperationPanel = () => {
  return (
    <OperationPanel
      title="DEPOT OPERATIONS"
      viewAllLink="/dashboard/containers"
      data={[
        {
          name: 'Moves in Progress',
          content: 'Containers being moved',
          value: 8,
          icon: <KeyboardTabRoundedIcon />,
          color: 'lightblue',
        },
        {
          name: 'Pending Puteaway',
          content: 'Awaiting placement',
          value: 12,
          icon: <LocalShippingOutlinedIcon />,
          color: 'lightyellow',
        },
        {
          name: 'Reefer Monitoring',
          content: 'Active reefers',
          value: 36,
          icon: <AcUnitRoundedIcon />,
          color: 'lightgreen',
        },
        {
          name: 'Maintenance',
          content: 'Equipment under maintenance',
          value: 3,
          icon: <ConstructionIcon />,
          color: 'lightred',
        },
      ]}
    />
  );
};

export const Mock2TablePanel = () => {
  return (
    <TablePanel
      title="TRUCK QUEUE"
      viewAllLink="/dashboard/containers"
      data={[
        {
          status: 'GATED_IN',
          isLoaded: false,
          licensePlateNumber: '01A234BC',
          driver: 'Ali Hasan',
          queueStatus: 'WAITING',
          eta: '10 min',
        },
        {
          status: 'GATED_OUT',
          isLoaded: true,
          licensePlateNumber: '80X999AA',
          driver: 'John Smith',
          queueStatus: 'LOADING',
          eta: '15 min',
        },
        {
          status: 'GATED_IN',
          isLoaded: false,
          licensePlateNumber: '01B987CD',
          driver: 'Rustam K.',
          queueStatus: 'WAITING',
          eta: '20 min',
        },
        {
          status: 'GATED_OUT',
          isLoaded: false,
          licensePlateNumber: '90D456EF',
          driver: 'Akmal T.',
          queueStatus: 'DONE',
          eta: '—',
        },
      ]}
      columns={[
        {
          body: (row) => (
            <TableIconCell align="left">
              {row.isLoaded ? (
                <LocalShippingRoundedIcon
                  sx={{ color: colors['--primary-100'], fontSize: 'var(--font-size-6)' }}
                />
              ) : (
                <LocalShippingOutlinedIcon
                  sx={{ color: colors['--primary-100'], fontSize: 'var(--font-size-6)' }}
                />
              )}
            </TableIconCell>
          ),
          column: 1,
        },
        {
          headText: () => 'Truck No.',
          bodyText: (row) => row.licensePlateNumber,
        },
        {
          headText: () => 'Driver',
          bodyText: (row) => row.driver,
        },
        {
          headText: () => 'Operation',
          headAlign: 'center',
          body: (row) => (
            <TableIconsCell align="left" withGap>
              {row.status === 'GATED_IN' ? (
                <KeyboardTabRoundedIcon
                  sx={{ fontSize: 'var(--font-size-6)', color: colors['--green-100'] }}
                />
              ) : (
                <StartRoundedIcon
                  sx={{ fontSize: 'var(--font-size-6)', color: colors['--primary-100'] }}
                />
              )}
              {row.status === 'GATED_IN' ? 'Gate In' : 'Gate Out'}
            </TableIconsCell>
          ),
        },
        {
          headText: () => 'Status',
          headAlign: 'center',
          body: (row) => (
            <TableCell align="left">
              <Chip
                size="small"
                label={
                  QueueStatusToChipLabels[row.queueStatus as keyof typeof QueueStatusToChipLabels]
                }
                color={
                  QueueStatusToChipColors[row.queueStatus as keyof typeof QueueStatusToChipColors]
                }
              />
            </TableCell>
          ),
        },
        {
          headText: () => 'ETA',
          headAlign: 'center',
          body: (row) => <TableCell align="center">{row.eta}</TableCell>,
        },
      ]}
    />
  );
};

export const Mock2OperationPanel = () => {
  return (
    <OperationPanel
      title="ALERTS"
      viewAllLink="/dashboard/containers"
      data={[
        {
          name: 'Overdue containers',
          content: '312 containers are overdue',
          value: 312,
          icon: <ErrorRoundedIcon />,
          withoutBg: true,
          color: 'lightred',
        },
        {
          name: 'Containers without location',
          content: '24 containers not placed',
          value: 24,
          icon: <WarningRoundedIcon />,
          withoutBg: true,
          color: 'lightyellow',
        },
        {
          name: 'Trucks waiting over 30 min',
          content: '14 trucks waiting over 30 min',
          value: 14,
          icon: <WarningRoundedIcon />,
          withoutBg: true,
          color: 'lightyellow',
        },
        {
          name: 'Maintenance required',
          content: '3 equipment require maintenance',
          value: 3,
          icon: <InfoRoundedIcon />,
          withoutBg: true,
          color: 'lightblue',
        },
      ]}
    />
  );
};
